﻿import { Intent } from '@blueprintjs/core';
import { ButtonSave, Button } from 'components/buttons';
import { FormSelect } from 'components/form-fields';
import { DateToLocal } from "components/dates";
import { DataDisplayTable } from "components/data-display";
import { ShowHide } from "components/layout";
import { AlertConfirm, Modal, NotificationToaster, NotificationInline } from 'components/notifications';
import React, { useEffect, useState, Fragment } from 'react';
import { JobService } from 'services';
import { UserAccess } from 'components/user-access/UserAccess';
import { useValidation } from "hooks/useValidation";
import { PageRestricted } from 'pages/errors/page-restricted';
import { useDirtyData } from "hooks/useDirtyData";
import PropTypes from 'prop-types';

export function CancelJob(
    {
        loading = false,
        closeCancelModal = null,
        showCancelModal = false,
        showJobDetails = false,
        job,
        jobIds,
        onJobCancelClose
    }
) {

    const [isCancelJobValid, cancelJobErrors, cancelJobValidate] = useValidation();
    const [showCancelJobModal, setShowCancelJobModal] = useState(false);
    const [savingJobCancel, setSavingJobCancel] = useState(false);    
    const [selectedCancelReason, setSelectedCancelReason] = useState("");
    const [showJobCancelConfirm, setShowJobCancelConfirm] = useState(false);
    const [headerSections, setHeaderSections] = useState([]);

    const requiredAction = ["Internal"];
    const isDataDirty = useDirtyData("", selectedCancelReason);
    const rules = [
        { fieldName: "cancelJobReason", required: true }
    ];

    const cancelReasons = [
        { id: '1', name: 'No longer required' },
        { id: '2', name: 'Location closed' },
        { id: '3', name: 'Service cancelled' },
        { id: '4', name: 'Reduction in service' },
        { id: '5', name: 'Admin error' }
    ];

    function setHeaders() {
        var headers = [
            {
                header: "Product Name",
                value: job.productName,
                key: 'product-type'
            },
            {
                header: "Job Status",
                value: job.state,
                key: 'job-status'
            },
            {
                header: "Visit Location",
                value: job.locationName,
                key: 'job-location'
            },
            {
                header: "Date",
                value: <DateToLocal format="DD/MM/YYYY HH:mm">
                    {job.scheduledDate}
                </DateToLocal>,
                key: 'scheduled-date'
            }
        ];
        setHeaderSections(headers);
    }

    useEffect(validateModel, [isDataDirty, selectedCancelReason]);

    useEffect(() => {
        cancelJobValidate(rules, selectedCancelReason);
    }, [selectedCancelReason]);

    useEffect(() => {
        if (job !== null && job !== undefined && showJobDetails) {
            setHeaders();
        }
        setShowCancelJobModal(showCancelModal);
    }, [showCancelModal, job, showJobDetails]);

    function validateModel() {
        if (isDataDirty) {
            cancelJobValidate(rules, selectedCancelReason);
        }
    }

    function closeCancelModalValue(changesMade) {
        if (onJobCancelClose !== null) {
            closeCancelModal(changesMade);
        };
        setShowCancelJobModal(false);
    }

    function cancelJob() {
        setShowJobCancelConfirm(true);
    }

    function onCancelJobConfirm() {
        if (!isCancelJobValid) {
            return;
        }


        setShowJobCancelConfirm(false);

        setSavingJobCancel(true);

        var bulkCancelDetails = {
            jobIds: jobIds,
            CancellationReasonId: parseInt(selectedCancelReason, 10)
        };

        JobService.cancelJobs(bulkCancelDetails).then(function () {
            NotificationToaster.show(Intent.SUCCESS, `The cancellation was successful.`);
        }, function (error) {
            NotificationToaster.show(Intent.DANGER, error);
        }).finally(function () {
            setSavingJobCancel(false);
            closeCancelModalValue(true);
        });
    }

    function onJobCancelCancel() {
        setShowJobCancelConfirm(false);
    }

    function onChangeReason(item) {
        setSelectedCancelReason(item.id);
    }

    function jobNoun() {
        return jobIds.length > 1 ? 'jobs' : 'job';
    }

    function pronoun() {
        return jobIds.length > 1 ? 'these' : 'this';
    }

    return (
        <UserAccess perform={requiredAction}
            yes={() => (
                <Fragment>
                    <Modal
                        isOpen={showCancelJobModal}
                        title={`Are you sure you want to cancel ${pronoun()} ${jobIds.length > 0 ? jobIds.length : ''} ${jobNoun()}?`}
                        showCloseButton={false}
                        clickOutsideClose={false}
                    >                        
                        <NotificationInline
                            allowClose={false}
                            show
                            text={`Please note, once you cancel ${pronoun()} ${jobNoun()}, the action cannot be reversed.`}
                            intent={Intent.WARNING}
                        />                                                        
                        <ShowHide
                            evaluator={showJobDetails}
                            show=
                            {
                                <DataDisplayTable
                                    data={headerSections}
                                    displayInRows={false}
                                    loading={loading}
                                />
                            }
                        >
                        </ShowHide>
                        

                        <FormSelect
                            disabled={false}
                            items={cancelReasons}
                            placeholder="Select a reason"
                            headingText="Cancel Job Reason:"
                            onItemSelect={onChangeReason}
                            loading={loading}
                            dangerHelperText={cancelJobErrors.cancelJobReason}
                            selectedValue={selectedCancelReason}
                            id="input-cancel-reason"
                        ></FormSelect>

                        <AlertConfirm
                            title="Please confirm"
                            isOpen={showJobCancelConfirm}
                            onConfirm={onCancelJobConfirm}
                            onCancel={onJobCancelCancel}
                        >
                            <p>Are you sure you want to cancel this job?</p>
                        </AlertConfirm>
                        
                        <div className="bp6-dialog-footer">
                            <div className="bp6-dialog-footer-actions">
                                <ButtonSave text={"Cancel Job"} icon={null} intent={Intent.DANGER} onClick={cancelJob} simpleDisabled={!isCancelJobValid} disabled={savingJobCancel}></ButtonSave>
                                <Button text="Close" onClick={() => { closeCancelModalValue(false); }} disabled={savingJobCancel}></Button>
                            </div>
                        </div>
                    </Modal>
                </Fragment>

            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

CancelJob.propTypes = {
    jobIds: PropTypes.array,
    loading: PropTypes.bool.isRequired,
    closeCancelModal: PropTypes.func,    
    showCancelModal: PropTypes.bool.isRequired,
    showJobDetails: PropTypes.bool
};